'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  FileInput,
  Input,
  Link,
  LoaderSpinner,
  Select
} from '@theme/components';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAppSelector } from '@akinon/next/redux/hooks';
import {
  useGetOrdersQuery,
  useSendContactMutation
} from '@akinon/next/data/client/account';
import { useStoreContactSubjects } from '@theme/hooks/useStoreContactSubjects';
import { ContactFormType } from '@akinon/next/types';
import * as yup from 'yup';
import { useLocalization } from '@akinon/next/hooks';
import ContactMaps from './contact-maps';

type ContactFormTypeExtended = ContactFormType & {
  name: string;
  lastName: string;
};

const contactFormSchema = (t) =>
  yup.object().shape({
    name: yup.string().required(t('account.contact.form.error.required')),
    lastName: yup.string().required(t('account.contact.form.error.required')),
    email: yup
      .string()
      .email(t('account.contact.form.error.email_valid'))
      .required(t('account.contact.form.error.required')),
    phone: yup
      .string()
      .transform((value: string) => value.replace(/_/g, '').replace(/ /g, ''))
      .length(11, t('account.contact.form.error.phone_length'))
      .required(t('account.contact.form.error.required')),
    subject: yup.string().required(t('account.contact.form.error.required')),
    message: yup
      .string()
      .required(t('account.contact.form.error.required'))
      .min(10, t('account.contact.form.error.message_length'))
      .label('message'),
    order: yup
      .string()
      .nullable()
      .notRequired()
      .when('subject', {
        is: (value) => value === '2',
        then: yup.string().required(t('account.contact.form.error.required'))
      }),
    file: yup.mixed()
  });

const ContactForm = () => {
  const { t } = useLocalization();
  const { data: session, status } = useSession();

  const filteredOrders = [
    { label: t('account.contact.form.order.placeholder'), value: '' }
  ];
  const filteredSubjects = [
    { label: t('account.contact.form.subject.placeholder'), value: '' }
  ];
  const [selectedSubject, setSelectedSubject] = useState(null);
  const { user_phone_format } = useAppSelector((state) => state.config);

  const {
    generalSubjects: contactSubject,
    isLoading: subjectLoading
  } = useStoreContactSubjects();
  const subjectSuccess = contactSubject.length > 0;

  const {
    data: orders,
    isLoading: ordersLoading,
    isSuccess: ordersSuccess
  } = useGetOrdersQuery(
    {},
    {
      skip: status === 'unauthenticated' || status === 'loading'
    }
  );
  

  const [sendContact, { isSuccess: formSuccess }] = useSendContactMutation();

  if (ordersSuccess) {
    orders?.results?.map((item) => {
      filteredOrders.push({ label: item.number, value: item.id.toString() });
    });
  }

  if (subjectSuccess) {
    contactSubject
      ?.filter((item) => {
        if (status === 'unauthenticated') {
          return !item.is_order_needed;
        }

        return item;
      })
      .forEach((item) => {
        filteredSubjects.push({ label: item.text, value: item.id });
      });
  }

  const {
    register,
    reset,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<ContactFormTypeExtended>({
    resolver: yupResolver(contactFormSchema(t))
  });
  const onSubmit: SubmitHandler<ContactFormTypeExtended> = (data) => {
    const formData = new FormData();

    Object.keys(data ?? {}).forEach((key) => {
      if (key === 'file' && data[key]) {
        formData.append(key, data[key][0]);
      } else if (key === 'name' || key === 'lastName') {
        return;
      } else if (data[key] && key !== 'file') {
        formData.append(key, data[key]);
      }
    });

    const fullName = `${data.name} ${data.lastName}`.trim();
    if (fullName) {
      formData.append('full_name', fullName);
    }

    sendContact(formData);
  };

  const handleChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  return (
    <div className="flex-1">
      <div className="flex flex-col lg:flex-row pt-10 lg:pt-30 lg:pb-20 gap-20 items-start">
        {formSuccess ? (
          <div className="flex-1">
            <div className="flex-1 w-full">
              <h3 className="text-3xl mb-5">
                {t('account.contact.form.success.title')}
              </h3>
              <p className="text-sm mb-4">
                {t('account.contact.form.success.description')}
              </p>
              <Link href={'/'} className="underline">
                {t('account.contact.form.success.button')}
              </Link>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex-1 w-full flex flex-col gap-[23px] items-start"
          >
            <div className="flex flex-col lg:flex-row items-start gap-6 w-full">
              <Input
                label={t('account.contact.form.name.placeholder')}
                className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] px-3 py-[15px] text-sm"
                {...register('name')}
                error={errors.name}
                required
              />
              <Input
                label={t('account.contact.form.lastName.placeholder')}
                {...register('lastName')}
                error={errors.lastName}
                className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] px-3 py-[15px] text-sm"
                required
              />
            </div>
            <div className="flex flex-col lg:flex-row items-start gap-6 w-full">
              <Input
                label={t('account.contact.form.email.placeholder')}
                type="email"
                className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] px-3 py-[15px] text-sm"
                {...register('email')}
                error={errors.email}
                required
              />
              <Input
                label={t('account.contact.form.phone.placeholder')}
                type="tel"
                className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] px-3 py-[15px] text-sm"
                format={user_phone_format.replace(/\9/g, '#')}
                mask="_"
                allowEmptyFormatting={true}
                control={control}
                {...register('phone')}
                error={errors.phone}
                required
              />
            </div>

            <div className="mb-5 relative w-full">
              {subjectLoading && <LoaderSpinner />}
              {subjectSuccess && (
                <>
                  <Select
                    className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] px-3  text-sm"
                    options={filteredSubjects}
                    {...register('subject')}
                    error={errors.subject}
                    onChange={handleChange}
                    label={t('account.contact.form.subject.title')}
                    required
                  />
                </>
              )}
            </div>

            {selectedSubject === '2' && (
              <div className="mb-5 relative">
                {ordersLoading && <LoaderSpinner />}
                {ordersSuccess && (
                  <>
                    <Select
                      className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] px-3  text-sm"
                      options={filteredOrders}
                      {...register('order')}
                      error={errors.order}
                      label={t('account.contact.form.order.title')}
                      required
                    />
                  </>
                )}
              </div>
            )}

            <div className="w-full">
              <label className="text-xs text-gray-800 mb-2 block">
                {t('account.contact.form.message.title')}{' '}
                <span className="text-secondary">*</span>
              </label>
              <textarea
                className="w-full bg-black-100/[0.02] border-gray/[0.3] border-[0.33px] p-3  text-sm"
                rows={7}
                name="message"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - Fix TS error
                {...register('message')}
              />
              {errors.message && (
                <div className="text-sm text-error">
                  {errors.message.message}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4 w-full lg:flex-row lg:items-center lg:justify-between">
              <p className="text-xs text-gray-800">
                {t('account.contact.form.kvkk_notice_before')}{' '}
                <a
                  href="/kisisel-verilerin-korunmasi-ve-gizlilik-politikasi"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {t('account.contact.form.kvkk_notice_link')}
                </a>{' '}
                {t('account.contact.form.kvkk_notice_after')}
              </p>
              <Button
                type="submit"
                className="h-[44px] font-medium text-sm bg-gray-250 text-gray rounded-full self-end lg:self-auto shrink-0 hover:bg-primary-black hover:text-white transition-colors duration-300"
              >
                {t('account.contact.form.submit_button')}
              </Button>
            </div>
          </form>
        )}

        <div className="flex-1 w-full">
          <h2 className="font-medium text-[2rem]">
            {t('common.contact_us.title')}
          </h2>
          <ContactMaps />
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
